import {computed, inject, Injectable, signal} from '@angular/core';
import {ApiService, IFile, IImgUrls} from './api.service';
import {catchError, EMPTY, tap} from 'rxjs';
import {LoadingService} from './loading.service';
import {AuthService} from './auth.service';
import {BriefsService} from './briefs.service';
import {ChatsService, IChatsAll} from './chats.service';
import {MessagesService} from './messages.service';
import {IMessage} from './messages-receivers/imessage.interface';
import {HelpersService} from './helpers.service';

@Injectable({
  providedIn: 'root'
})
export class DisputesApiService {
  loadingService = inject(LoadingService);
  apiService = inject(ApiService);
  authService = inject(AuthService);
  messagesService = inject(MessagesService)

  User = this.authService.user

  MessagesReceiver = this.messagesService.disputeReceiver

  CreateDisputes(id: number) {
    return this.apiService.SendPost(`/${this.User()?.role?.toLowerCase()}/dispute-chat/by-integration/${id}`, this.loadingService.StatesNames.DISPUTES_CREATE);
  }

  GetDisputesList(
    id: number,
    limit: number,
    page: number,
  ) {
    const params: Record<string, string> = {
      limit: limit.toString(),
      page: page.toString(),
    };

    const queryString = '?' + new URLSearchParams(params).toString();
    return this.apiService.SendGet(`/${this.User()?.role?.toLowerCase()}/brief/${id}/dispute-chat${queryString}`, this.loadingService.StatesNames.DISPUTES_GET);
  }

  GetDisputesItem(id: number) {
    return this.apiService.SendGet(`/${this.User()?.role?.toLowerCase()}/dispute-chat/${id}`, this.loadingService.StatesNames.DISPUTES_GET_ITEM);
  }

  CloseDisputes(id: any) {
    return this.apiService.SendPost(`/user/dispute-chat/${id}/close`, this.loadingService.StatesNames.DISPUTES_CLOSE);
  }

  GetMessagesList(chatId: number, offsetMessageId: number | null = null) {
    return this.apiService.SendGet(`/${this.User()?.role?.toLowerCase()}/dispute-chat/${chatId}/history?${offsetMessageId ? `offsetMessageId=${offsetMessageId}` : ''}`,);
  }

  DownloadMessageFile(id: number) {
    return this.apiService.SendGetBlobImg(`/dispute-message-storage/${id}`, this.loadingService.StatesNames.MESSAGE_DOWNLOAD_FILE);
  }
}

@Injectable({
  providedIn: 'root'
})
export class DisputesService {
  disputesApiService = inject(DisputesApiService);
  briefsService = inject(BriefsService);
  helpersService = inject(HelpersService);
  chatsService = inject(ChatsService);

  Messages = this.disputesApiService.MessagesReceiver.eventsList

  DisputesList = signal<IChatsAll[]>([]);
  Dispute = signal<IChatsAll>({} as IChatsAll);
  totalPages = signal<number>(0);
  totalElements = signal<number>(0);

  constructor() {
  }

  createDisputes(id: number, disputeChatsCount: number) {
    return this.disputesApiService.CreateDisputes(id).pipe(
      tap((res) => {
        this.updateDispute(res);
        this.DisputesList.set([...this.DisputesList(), res]);
        this.briefsService.updateBrief({disputeChatsActiveExists: true, disputeChatsCount: disputeChatsCount + 1});
      })
    )
  }

  getDisputesList(
    {id, limit, page}:
      {
        id: number;
        limit: number;
        page: number;
      }
  ) {
    return this.disputesApiService.GetDisputesList(id, limit, page).pipe(
      tap((res) => {
        this.DisputesList.set(res['content']);
        this.totalPages.set(res['page']['totalPages']);
        this.totalElements.set(res['page']['totalElements']);
      })
    )
  }

  getDisputesItem(id: number) {
    return this.disputesApiService.GetDisputesItem(id).pipe(
      tap((res) => {
        this.Dispute.set(res);
        this.chatsService.countListAll.set(this.chatsService.countListAll() - res['unreadCount']);
        this.chatsService.countListDispute.set(this.chatsService.countListDispute() - res['unreadCount']);
      })
    )
  }

  closeDisputes(id: number) {
    return this.disputesApiService.CloseDisputes(id).pipe(
      tap((res) => {
        this.updateDispute({isActive: false})
      })
    )
  }

  clearDispute(): void {
    this.Dispute.set({} as IChatsAll);
  }

  updateDispute(data: any): void {
    this.Dispute.set({
      ...this.Dispute(),
      ...data
    });
  }

  getMessages(chatId: number, offsetMessageId: number | null = null) {
    return this.disputesApiService.GetMessagesList(chatId, offsetMessageId).pipe(
      tap(
        (res) => {
          let ms: IMessage[] = res.content.map((x: {
            id: number,
            text: string,
            createdAt: string,
            isRead: boolean,
            sender: {
              id: number,
              name: string,
              role: string
            }
            files: {
              id: number,
              name: string,
              size: number,
              url: string,
              blurhash: string,
              contentType: string
            }[]
          }) => {
            const r: IMessage = {
              id: x.id,
              chatId: chatId,
              content: {
                text: x.text,
                files: x.files,
                isRead: x.isRead,
                sender: x.sender
              },
              createdAt: x.createdAt,
              updatedAt: x.createdAt
            }
            return r
          });

          for (let i = 0; i < ms.length; i++) {
            if (ms[i].content?.files) {
              for (let j = 0; j < ms[i].content.files!.length; j++) {
                ms[i].content.files![j].size = this.helpersService.formatBytes(ms[i].content.files![j].size as number)
              }
            }
          }

          this.Messages.set([...ms, ...this.Messages()]);
        }
      )
    )
  }

  sendMessage(message: {
    disputeChatId: number
    text: string | null,
    files: IFile[] | null,
    event: 'NEW_DISPUTE_MESSAGE'
  }) {
    this.disputesApiService.MessagesReceiver.sendMessage(
      {
        messageToHistory: this.disputesApiService.MessagesReceiver.outcomingEventToIMessage(
          message
        ),
        messageSend: {
          event: message.event,
          data: {
            disputeChatId: message.disputeChatId,
            text: message.text,
            files: message.files?.map((x: any) => x.id) ?? [],
          }
        }
      }
    );
  }

  downloadMessageFile(id: number) {
    return this.disputesApiService.DownloadMessageFile(id).pipe(
      tap((res) => {
        return res;
      })
    )
  }
}

